import map from 'lodash/map';
import filter from 'lodash/filter';
import some from 'lodash/some';
import deburr from 'lodash/deburr';
import uniq from 'lodash/uniq';
import defaults from 'lodash/defaults';
import capitalize from 'lodash/capitalize';
import toString from 'lodash/toString';
import difference from 'lodash/difference';
import includes from 'lodash/includes';
import {MODE_RACCORDEMENT, NATURE_PERTURBATION} from '../modules/common/model';

/**
 * Construction de l'objet de demande PRTV à partir d'une demande antenniste
 *
PRTV (antenniste)
{
  "date_anomalie" : Date "AAAA-MM-JJ", Obligatoire
  "descriptif" : Chaîne de caractères
  "impact" : Chaîne de caractères
  "signalant" : (Obligatoire) {
    "type_signalant" : Chaîne de caractères, Obligatoire
    "denomination_commerciale" : Chaîne de caractères
    "siret" : Chaîne de caractères
    "civilite" : Chaîne de caractères, Obligatoire(M ou MME)
    "nom" : Chaîne de caractères, Obligatoire
    "prenom" : Chaîne de caractères, Obligatoire
    "telephone1" : Chaîne de caractères
    "adresse" : (Obligatoire) {
      "numero" : Chaîne de caractères
      "voie" : Chaîne de caractères, Obligatoire
      "complement" : Chaîne de caractères
      "boite_postale" : Chaîne de caractères
      "ville" : Chaîne de caractères, Obligatoire
      "code_postal" : Chaîne de caractères, Obligatoire
      "cedex" : Chaîne de caractères
      "adresse_structuree" : Chaîne de caractères, obligatoire
    }
  }
  "multiplex_impactes" : ["1","2", …] (voir liste des multiplex, utilisé l’id)
  "adresses_impactees" : (Obligatoire – Au moins une adresse) [{
    "adresse" : {
      "numero" : Chaîne de caractères
      "voie" : Chaîne de caractères, Obligatoire
      "ville" : Chaîne de caractères, Obligatoire
      "code_postal" : Chaîne de caractères, Obligatoire
      "adresse_structuree" : Chaîne de caractères, obligatoire
    }
    "type_perturbation" : Entier, (voir liste des types de perturbation, utiliser l’id), Obligatoire
    "type_defaut" : Entier, (voir liste des défauts, utiliser l’id), Obligatoire
    "id_commune" : Entier, (voir liste des communes, utiliser l’id), obligatoire
  }]
}
*/
function demandeAntennisteToPrtv(demande) {
  return {
    dateConstat: demande.accueilInterface2.datePerturbation,
    descriptif: demande.constat.descriptif,
    impact: demande.constat.impactSuppose,
    emetteur: '' + demande.constat.emetteurtnt.emetteurtnt.id  + '',
    signalant: defaults(
      {adresse: formatAdresse(demande.contact.adresse, demande.contact.complementAdresse, demande.contact.adresse.voie)},
      formatSignalant(demande.typePersonne, demande.contact)),
    multiplexImpactes: map(demande.constat.multiplex, 'id'),
    adressesImpactees: [{
      idCommune: demande.accueilInterface2.adresse.commune.id,
      inseeCommune: demande.accueilInterface2.adresse.commune.insee,
      typeDefaut: demande.constat.typeProbleme === 'Permanent' ? 1 : 2,
      typePerturbation: demande.constat.typePerturbation.id,
      adresse: formatAdresse(demande.accueilInterface2.adresse, {}, demande.voieShort),
      civilite: 'M',
      nom: 'Indéterminé',
      prenom: 'Indéterminé'
    }]
  };
}

/**
 * Construction de l'objet de demande PRTV à partir d'une demande
 *
PRTV
{
  "date_anomalie" : Date "AAAA-MM-JJ", Obligatoire
  "complement" : Chaîne de caractères
  "voisinsConcernes" : Chaîne de caractères
  "perturbateur" : Chaîne de caractères
  "perturbateurElement" : Chaîne de caractères
  "perturbateurAmiable" : Chaîne de caractères
  "perturbateurCoordonnees" : Chaîne de caractères
  "perturbateurAutre" : Chaîne de caractères
  "stationLteConfirmee" : Chaîne de caractères
  "signalant" : (Obligatoire) {
    "type_signalant" : Chaîne de caractères, Obligatoire
    "civilite" : Chaîne de caractères, Obligatoire(M ou MME)
    "nom" : Chaîne de caractères, Obligatoire
    "prenom" : Chaîne de caractères, Obligatoire
    "telephone1" : Chaîne de caractères
    "adresse" : (Obligatoire) {
      "numero" : Chaîne de caractères
      "voie" : Chaîne de caractères, Obligatoire
      "complement" : Chaîne de caractères
      "boite_postale" : Chaîne de caractères
      "ville" : Chaîne de caractères, Obligatoire
      "code_postal" : Chaîne de caractères, Obligatoire
      "cedex" : Chaîne de caractères
      "adresse_structuree" : Chaîne de caractères, obligatoire
    }
  }
  "multiplex_impactes" : ["1","2", …] (voir liste des multiplex, utilisé l’id)
  "adresses_impactees" : (Obligatoire – Au moins une adresse) [{
    "adresse" : {
      "numero" : Chaîne de caractères
      "voie" : Chaîne de caractères, Obligatoire
      "ville" : Chaîne de caractères, Obligatoire
      "code_postal" : Chaîne de caractères, Obligatoire
      "adresse_structuree" : Chaîne de caractères, obligatoire
    }
    "type_perturbation" : Entier, (voir liste des types de perturbation, utiliser l’id), Obligatoire
    "type_defaut" : Entier, (voir liste des défauts, utiliser l’id), Obligatoire
    "id_commune" : Entier, (voir liste des communes, utiliser l’id), obligatoire
  }]
}
*/
function demandeToPrtv(demande) {
  const adresseImpactee = demande.accueilInterface2.adresse;
  return {
    numeroAnfr: demande.numeroAnfr,
    checkCode: demande.checkCode,
    dateConstat: demande.accueilInterface2.datePerturbation,
    complement: demande.prtvInterface1 ? (demande.prtvInterface1.complement ?
    toString((demande.prtvInterface1.infoChaineLocal ? demande.prtvInterface1.infoChaineLocal + '; ' : '') +
    demande.prtvInterface1.complement).trim() : demande.prtvInterface1.infoChaineLocal) : null,
    signalant: defaults(
      {adresse: formatAdresse(demande.accueilInterface2.adresse, demande.accueilInterface2.complementAdresse, demande.voieShort)},
      formatSignalant(demande.typePersonne, demande.prtvInterface1)),
    multiplexImpactes: demande.prtvInterface1  ? (demande.prtvInterface1.isAdresseDomCom ? (demande.prtvInterface1.chainesRecues === 'OUI' ? [] :
      multiplexRom1(demande.accueilInterface2.multiplexAll)) :
      multiplexFromChaines(demande.prtvInterface1.chaines, demande.accueilInterface2.multiplex)) : [] ,
    voisinsConcernes: demande.prtvInterface1 ? demande.prtvInterface1.voisinsConcernes : '',
    perturbateur: demande.prtvInterface1 ? demande.prtvInterface1.perturbateur : '',
    perturbateurElement: demande.prtvInterface1 ? demande.prtvInterface1.perturbateurElement : '',
    perturbateurAmiable: demande.prtvInterface1 ? demande.prtvInterface1.perturbateurAmiable : '',
    perturbateurCoordonnees: demande.prtvInterface1 ? demande.prtvInterface1.perturbateurCoordonnees : '',
    perturbateurAutre: demande.prtvInterface1 ? demande.prtvInterface1.perturbateurAutre : '',
    stationLteConfirmee: demande.prtvInterface1 ?
      (demande.signalementAntenniste &&
        demande.signalementAntenniste.natureProbleme &&
        demande.signalementAntenniste.natureProbleme === NATURE_PERTURBATION.BROUILLAGE_4G_5G) ||
      !demande.prtvInterface1.demandeEnquetePapier
      : demande.defaultSuspicionLte,
    demandeEnquete: demande.prtvInterface1 ? demande.prtvInterface1.demandeEnquetePapier : false,
    adressesImpactees: [
      defaults({
          adresse: formatAdresse(adresseImpactee, {}, demande.voieShort),
          idCommune: adresseImpactee.commune.id,
          inseeCommune: adresseImpactee.commune.insee,
          typeDefaut: 3,
          typePerturbation: demande.prtvInterface1 && demande.prtvInterface1.typePerturbation ? demande.prtvInterface1.typePerturbation.id : 10,
        }, formatCoordoonees(demande.accueilInterface2))
    ],
  };
}

/*Demande de PRTV pour la demande d'enquete
{
  receivedStation: station selectionnee parmi celles que l'utilisateur reçoit via
  l'appel a vigispectre pour recevoir les stations
  otherSelectedStationName: station alternative entree manuellement par l'utilisateur
  validStation: station valide (soit celle reçue et selectionee, soit celle entree manuellement)
  isNoOrientation: booleen qui repond a : l'utilisateur connait l'orientation de son antenne ?
  perturbationsValue: valeur de la perturbation du signal
  isConstructionKnowledge: booleen qui repond a : l'utilisateur connait le type de construction (immeuble, eolienne)
  qui pourrait causer les problemes de reception
  constructionType: type de la construction (immeuble, eolienne)
  isCloseAround: booleen qui repond a : l'edifice se situe a moins de 5 km de l'antenne
  firstPerturbationDate: date a laquelle les premieres perturbations sont arrivees
}
*/

function demandeToPrtvDemandeEnquete(demande) {

  let multiplex;
  if (demande.prtvInterface1) {
    const {complement, infoChaineLocal} = demande.prtvInterface1;

    const texteComplement = [
      infoChaineLocal,
      complement
    ].filter(Boolean).join('; ').trim();

    const isChaineLocalRecu = texteComplement === 'Chaîne(s) locale(s) reçue(s)';
    const hasUncheckedChannel = demande.prtvInterface5.channels.station.every(item => item.switchValue);
    const isChaineInvalid = isChaineLocalRecu && hasUncheckedChannel;

    if (!isChaineInvalid) {
      multiplex = demande.accueilInterface2.multiplexAll.find(i => i.code === 'R15').id;
    }
  }

  return {
    numeroAnfr: demande.numeroAnfr,
    checkCode: demande.checkCode,
    informationsReclamation: [
      defaults({
        connexionQuality: demande.prtvInterface5 ? demande.prtvInterface5.connexionQuality : '',
        channelsArray: demande.prtvInterface5 ? demande.prtvInterface5.channels.station : [],
        channelsString: demande.prtvInterface5 ?  demande.prtvInterface5.channels.station ? demande.prtvInterface5.channels.station
              .filter(channel=>channel.switchValue)
              .map(channel => channel.name)
              .join(', ') : '' : '',
        receivedStation: demande.prtvInterface5 ? demande.prtvInterface5.receivedStation : '',
        otherSelectedStationName: demande.prtvInterface5 ? demande.prtvInterface5.otherSelectedStationName : '',
        validStation: demande.prtvInterface5 ? demande.prtvInterface5.validStation : '',
        isNoOrientation: demande.prtvInterface5 ? demande.prtvInterface5.isNoOrientation : true,
        perturbationsValue: demande.prtvInterface5 ? demande.prtvInterface5.perturbationsValue : '',
        isConstructionKnowledge: demande.prtvInterface5 ? demande.prtvInterface5.isConstructionKnowledge : false,
        constructionType: demande.prtvInterface5 ? demande.prtvInterface5.constructionType : '',
        isCloseAround: demande.prtvInterface5 ? demande.prtvInterface5.is5KmAround : false,
        firstPerturbationDate: demande.prtvInterface5 ? demande.prtvInterface5.firstPerturbationDate : null,
        multiplexLocalImpactes: multiplex,
      })
    ]
  };
}

/* Demande de PRTV pour la demande d'enquete, ajout de la facture et des informations de l'antenniste
*/

function demandeAvecFactureDemandeEnquete(demande) {
  return {
    numeroAnfr: demande.numeroAnfr,
    checkCode: demande.checkCode,
    informationsDemandeEnqueteAntenniste: [
      defaults({
        deAdresseAntenniste: demande.prtvDepotFacture2 ? demande.prtvDepotFacture2.adresseContact : '',
        deDenominationCommercialeAntenniste: demande.prtvDepotFacture2 ? demande.prtvDepotFacture2.denomination : '',
        factureFile: demande.prtvDepotFacture2 ? demande.prtvDepotFacture2.factureFile : '',
        factureFileName: demande.prtvDepotFacture2 ? demande.prtvDepotFacture2.factureFileName : '',
        isFactureOk: demande.prtvDepotFacture2 ? demande.prtvDepotFacture2.isFactureOk : false,
        deEmailAntenniste: demande.prtvDepotFacture2 ? demande.prtvDepotFacture2.mailContact : '',
        deNomAntenniste: demande.prtvDepotFacture2 ? demande.prtvDepotFacture2.nomContact : '',
        autresFactures: demande.prtvDepotFacture2 ? demande.prtvDepotFacture2.autresFactures : [],
        deNumeroTelephoneAntenniste: demande.prtvDepotFacture2 ? demande.prtvDepotFacture2.telContact : '',
      })
    ]
  };
}

/**
LTE
{
  "date_anomalie" : Date "AAAA-MM-JJ", Obligatoire
  "signalant" : (Obligatoire) {
    "type_signalant" : Chaîne de caractères (Obligatoire), Obligatoire
    "civilite" : Chaîne de caractères, Obligatoire
    "nom" : Chaîne de caractères, Obligatoire
    "prenom" : Chaîne de caractères, Obligatoire
    "telephone1" : Chaîne de caractères (Obligatoire si telephone2 et courriel non renseigné)
    "telephone2" : Chaîne de caractères (Obligatoire si telephone1 et courriel non renseigné)
    "courriel" : Chaîne de caractères (Obligatoire si telephone1 et telephone2 non renseigné)
    "adresse" : (Obligatoire) {
      "numero" : Chaîne de caractères
      "voie" : Chaîne de caractères, Obligatoire
      "complement" : Chaîne de caractères
      "boite_postale" : Chaîne de caractères
      "ville" : Chaîne de caractères, Obligatoire
      "code_postal" : Chaîne de caractères, Obligatoire
      "cedex" : Chaîne de caractères
      "adresse_structuree" : Chaîne de caractères, obligatoire
   }
  }
  "multiplex_impactes" : ["1","2", …] (voir liste des multiplex, utilisé l’id)
  "adresses_impactees : (Obligatoire – Au moins une adresse) [
    {
      "civilite" : Entier (voir liste des civilités, utiliser l’id), obligatoire
      "nom" : Chaîne de caractères, Obligatoire
      "prenom" : Chaîne de caractères, Obligatoire
      "telephone1" : Chaîne de caractères
      "telephone2" : Chaîne de caractères
      "courriel" : Chaîne de caractères
      "adresse" : {
        "numero" : Chaîne de caractères
        "voie" : Chaîne de caractères, Obligatoire
        "ville" : Chaîne de caractères, Obligatoire
        "code_postal" : Chaîne de caractères, Obligatoire
        "adresse_structuree" : Chaîne de caractères, obligatoire
      }
      "id_commune" : Entier, (voir liste des communes, utiliser l’id), obligatoire
      "type_perturbation" : Entier, (voir liste des types de perturbation, utiliser l’id), Obligatoire
      "type_defaut" : Entier, (voir liste des défauts, utiliser l’id), Obligatoire
    }, {
       …
    }
  ]
  "operateur_lte" : Chaine de caractères, (Voir liste des opérateurs LTE, utiliser le code), obligatoire
// -> code_operateur_lte de la première antenne (station_lte)
  "type_raccordement" : Chaîne de caractères  (Individuel ou Collectif), obligatoire
  "station_lte" : [
    {
      "numero_anfr" : Chaîne de caractères, obligatoire
      "code_antenne_operateur" : Chaîne de caractères, obligatoire
      "code_operateur_lte" : Chaine de caractères, (Voir liste des opérateurs LTE, utiliser le code), obligatoire
      "techno" : Chaîne de caractères (LTE700 ou LTE800)
      "azimut" : Chaîne de caractères, obligatoire
      "puissance_recue" : Chaîne de caractères, obligatoire
      "distance" : Chaîne de caractères, obligatoire
    },{
      …
    }
  ]
}
 */
function demandeToLte(demande) {
  const adresseImpactee = demande.accueilInterface2.adresse;
  let antennes = '';
  if (demande.signalementAntenniste && demande.signalementAntenniste.antennes) {
    antennes = demande.signalementAntenniste.antennes.data;
  } else if (demande.dysfonctionnementInterface && demande.dysfonctionnementInterface.antennes) {
    antennes = demande.dysfonctionnementInterface.antennes.data;
  }
  return {
    dateConstat: demande.accueilInterface2.datePerturbation,
    signalant: defaults(
      {adresse: formatAdresse(demande.accueilInterface2.adresse, demande.accueilInterface2.complementAdresse, demande.voieShort)},
      formatSignalant(demande.typePersonne, demande.lteInterface1)),
    multiplexImpactes: multiplexFromChaines(demande.accueilInterface2.chaines, demande.accueilInterface2.multiplex),
    adressesImpactees: [
      defaults({
          adresse: formatAdresse(adresseImpactee, {}, demande.voieShort),
          idCommune: adresseImpactee.commune.id,
          inseeCommune: adresseImpactee.commune.insee,
          typeDefaut: 3,
          typePerturbation: 10,
        }, formatCoordoonees(demande.lteInterface1))
    ],
    typeRaccordement: demande.lteInterface1.modeRaccordement ?
     capitalize(demande.lteInterface1.modeRaccordement) : capitalize(MODE_RACCORDEMENT.COLLECTIF),
    stationLte: antennes
  };
}

function multiplexRom1(allMutiplex) {
  const muxInclud = ['ROM1'];
  const multiplex = filter(allMutiplex, m=> includes(muxInclud, m.code));
  return uniq(map(multiplex, 'id'));
}

function multiplexFromChaines(chaines, allMultiplex) {
  // On récupère les multiplex qui marchent et on fait la difference pour envoyer les mux qui marchent pas
  const muxConcernes = difference(allMultiplex, filter(allMultiplex, (m) =>
   m.chainesActives.length && some(m.chainesActives, (c) => some(chaines, (c2) => c.id === c2.id))));

  return uniq(map(muxConcernes, 'id'));
}

function formatSignalant(typeSignalant, data = {}) {
  return defaults({typeSignalant}, formatCoordoonees(data));
}

function formatCoordoonees(data) {
  return {
    denominationCommerciale: data.societe,
    siret: data.siret,
    civilite: data.civilite ? data.civilite : 'MME' ,
    nom: data.nom ? data.nom : 'anonyme',
    prenom: data.prenom ? data.prenom : 'anonyme',
    telephone1: data.telephone1 ? data.telephone1 : '0000000000',
    telephone2: data.telephone2,
    courriel: data.courriel,
  };
}

function formatAdresse(adresse = {}, complementAdresse = {}, voieName = {}) {
  const adresseStruct = deburr(`${adresse.numero || ''} ${voieName} ${adresse.codePostal.codepostal} ${adresse.commune.nom}`);
  return {
    numero: deburr(adresse.numero),
    voie: deburr(voieName),
    complement: deburr(complementAdresse.complement),
    ville: deburr(adresse.commune.nom),
    codePostal: deburr(adresse.codePostal.codepostal),
    adresseStructuree: deburr(adresseStruct)
  };
}

export default {demandeAntennisteToPrtv, demandeToPrtv, demandeToLte, demandeToPrtvDemandeEnquete, demandeAvecFactureDemandeEnquete};



// WEBPACK FOOTER //
// ./src/utils/demande.util.js